import _round from 'lodash/round';
import { useIntl } from 'react-intl';
import { RefinementCtx } from 'zod';

import { useBudgetType } from '@/hooks/use-budget-type';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { getChannelName } from '@/utils/channel-names';
import { durationInDays } from '@/utils/date-util';

import { BudgetAllocation, BudgetType } from '../types';
import { getChannelDefaults } from '../utils/channel-defaults';
import { MIN_CHANNEL_BUDGET, ROUNDING_TOLERANCE } from './constants';

type ChannelData = {
  startsAt: string;
  endAt: string | null;
  budgetType: BudgetType;
  openingBudgets: Record<string, number | null>;
  budgetAllocation: BudgetAllocation;
  channelBudget: number | null;
  slug: string;
};

/**
 * Hook that provides channel validation functions with i18n messages
 */
export function useChannelRefinementValidators() {
  const intl = useIntl();
  const { calculateTotalBudget } = useBudgetType();
  const { formatCurrency } = useCurrencySymbol();

  const messages = {
    minimumBudgetForChannel: (channelName: string, minRequiredBudget: number) =>
      intl.formatMessage(
        {
          defaultMessage:
            'Minimum budget for {channelName} is {minRequiredBudget}',
          description:
            'Error message when opening budget is less than the minimum required budget',
        },
        {
          channelName,
          minRequiredBudget: formatCurrency(minRequiredBudget),
        },
      ),
    openingBudgetsExceedChannelBudget: (totalOpeningBudgets: number) =>
      intl.formatMessage(
        {
          defaultMessage:
            'Opening allocation of {totalOpeningBudgets} exceeds the total spend. Lower the opening allocation or increase the total spend.',
          description:
            'Error message when opening budgets exceed channel budget',
        },
        {
          totalOpeningBudgets: formatCurrency(totalOpeningBudgets),
        },
      ),
    openingBudgetsLessThanChannelBudget: (totalOpeningBudgets: number) =>
      intl.formatMessage(
        {
          defaultMessage:
            'Opening allocation of {totalOpeningBudgets} is less than the total spend. Increase the opening allocations or decrease the total spend.',
          description:
            'Error message when opening budgets are less than the total spend',
        },
        {
          totalOpeningBudgets: formatCurrency(totalOpeningBudgets),
        },
      ),
    minimumBudgetPerOpeningForChannel: (
      channelName: string,
      perOpeningBudget: number,
      minRequiredBudget: number,
    ) =>
      intl.formatMessage(
        {
          defaultMessage:
            'Per opening budget of {perOpeningBudget} is less than the minimum required budget of {minRequiredBudget} for {channelName}',
          description:
            'Error message when channel budget is less than the minimum required budget',
        },
        {
          channelName,
          perOpeningBudget: formatCurrency(perOpeningBudget),
          minRequiredBudget: formatCurrency(minRequiredBudget),
        },
      ),
  };

  /**
   * Validates that opening budgets meet minimum requirements for per-funnel allocation
   */
  const validatePerFunnelMinimumBudgets = (
    data: ChannelData,
    ctx: RefinementCtx,
  ) => {
    const { budgetType, openingBudgets, slug, budgetAllocation } = data;

    if (budgetAllocation !== 'per-funnel' || !data.endAt) return;

    Object.entries(openingBudgets).forEach(([openingId, budget]) => {
      if (!budget) return;

      const openingBudget = calculateTotalBudget({
        budgetType,
        budgetPerUnit: budget,
        startDate: data.startsAt,
        endDate: data.endAt as string,
      });

      const minRequiredBudget =
        getChannelDefaults(slug).minBudget ?? MIN_CHANNEL_BUDGET;

      if (openingBudget < minRequiredBudget) {
        ctx.addIssue({
          code: 'custom',
          message: messages.minimumBudgetForChannel(
            getChannelName(slug),
            minRequiredBudget,
          ),
          path: ['openingBudgets', openingId],
        });
      }
    });
  };

  /**
   * Validates that total opening budgets match channel budget (with tolerance for rounding)
   */
  const validateOpeningBudgetsTotalMatchesChannelBudget = (
    data: ChannelData,
    ctx: RefinementCtx,
  ) => {
    if (data.budgetAllocation === 'evenly') return;

    const totalOpeningBudgets = Object.values(data.openingBudgets).reduce(
      (acc: number, budget) => {
        return acc + (budget ?? 0);
      },
      0,
    );

    const channelBudget = Number(data.channelBudget);

    // Case 1: Opening budgets exceed channel budget (beyond tolerance)
    if (totalOpeningBudgets > channelBudget + ROUNDING_TOLERANCE) {
      ctx.addIssue({
        code: 'custom',
        message: messages.openingBudgetsExceedChannelBudget(
          _round(totalOpeningBudgets, 2),
        ),
        path: ['channelBudget'],
      });
    }

    // Case 2: Opening budgets are less than channel budget (beyond tolerance)
    if (totalOpeningBudgets < channelBudget - ROUNDING_TOLERANCE) {
      ctx.addIssue({
        code: 'custom',
        message: messages.openingBudgetsLessThanChannelBudget(
          _round(totalOpeningBudgets, 2),
        ),
        path: ['channelBudget'],
      });
    }
  };

  /**
   * Validates minimum budget per opening for even allocation
   */
  const validateEvenAllocationMinimumBudget = (
    data: ChannelData,
    ctx: RefinementCtx,
  ) => {
    const {
      channelBudget,
      openingBudgets,
      slug,
      budgetType,
      budgetAllocation,
    } = data;

    if (!data.endAt || budgetAllocation !== 'evenly') return;

    const perOpeningBudget =
      calculateTotalBudget({
        budgetType,
        budgetPerUnit: channelBudget,
        startDate: data.startsAt,
        endDate: data.endAt,
      }) / Object.keys(openingBudgets).length;

    const minRequiredBudget =
      getChannelDefaults(slug).minBudget ?? MIN_CHANNEL_BUDGET;

    if (perOpeningBudget < minRequiredBudget) {
      ctx.addIssue({
        code: 'custom',
        message: messages.minimumBudgetPerOpeningForChannel(
          getChannelName(slug),
          _round(perOpeningBudget, 2),
          minRequiredBudget,
        ),
        path: ['channelBudget'],
      });
    }
  };

  return {
    validatePerFunnelMinimumBudgets,
    validateOpeningBudgetsTotalMatchesChannelBudget,
    validateEvenAllocationMinimumBudget,
  };
}

/**
 * Validates that end date is after start date
 * This is a pure function with no i18n dependencies, so it's exported directly
 */
export function validateEndDateIsAfterStartDate(data: ChannelData) {
  if (!data.endAt || !data.startsAt) return true;

  const startDate = new Date(data.startsAt);
  startDate.setHours(0, 0, 0, 0);
  const endDate = new Date(data.endAt);
  endDate.setHours(0, 0, 0, 0);

  return endDate > startDate;
}

/**
 * Validates that the campaign duration does not exceed the channel's maximum
 * duration (when one is configured). Pure function with no i18n dependencies.
 */
export function validateMaxDuration(data: ChannelData) {
  const { maxDuration } = getChannelDefaults(data.slug);
  if (!data.endAt || !data.startsAt || maxDuration == null) return true;

  return durationInDays(data.startsAt, data.endAt) <= maxDuration;
}
